// 이미지 업로드 서비스 - Google Cloud Storage 연동

const API_BASE_URL = process.env.REACT_APP_CONTENT_API_URL || 'https://sslmo-dambi.com/api/v1';

export interface ImageUploadResult {
  success: boolean;
  public_url?: string;
  filename?: string;
  size?: number;
  dimensions?: {
    width: number;
    height: number;
  };
  error?: string;
}

export interface ImageUploadRequest {
  base64_data: string;
  filename_prefix?: string;
  alt_text?: string;
}

class ImageUploadService {
  /**
   * Upload base64 image to Google Cloud Storage
   */
  async uploadBase64Image(request: ImageUploadRequest): Promise<ImageUploadResult> {
    try {
      console.log('Uploading image to GCS...', { 
        prefix: request.filename_prefix,
        size: request.base64_data.length 
      });

      const response = await fetch(`${API_BASE_URL}/images/upload-base64`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          base64_data: request.base64_data,
          filename_prefix: request.filename_prefix || 'blog_image',
          alt_text: request.alt_text || ''
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Upload failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('Image upload successful:', result);

      return result;
    } catch (error) {
      console.error('Image upload failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown upload error'
      };
    }
  }

  /**
   * Upload multiple base64 images in batch
   */
  async uploadMultipleBase64Images(requests: ImageUploadRequest[]): Promise<ImageUploadResult[]> {
    try {
      console.log(`Uploading ${requests.length} images to GCS...`);

      const response = await fetch(`${API_BASE_URL}/images/batch-upload-base64`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requests),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Batch upload failed: ${response.status}`);
      }

      const results = await response.json();
      console.log('Batch image upload completed:', results);

      return results;
    } catch (error) {
      console.error('Batch image upload failed:', error);
      return requests.map(() => ({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown upload error'
      }));
    }
  }

  /**
   * Convert blob URL to base64 and upload to GCS
   */
  async uploadBlobImage(blobUrl: string, filename_prefix?: string): Promise<ImageUploadResult> {
    try {
      // Fetch blob data
      const response = await fetch(blobUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch blob data');
      }
      
      const blob = await response.blob();
      
      // Convert blob to base64
      const base64 = await this.blobToBase64(blob);
      
      // Upload to GCS
      return await this.uploadBase64Image({
        base64_data: base64,
        filename_prefix: filename_prefix || 'blob_image'
      });
    } catch (error) {
      console.error('Blob image upload failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Blob upload failed'
      };
    }
  }

  /**
   * Convert Blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Extract base64 images from HTML content and upload them to GCS
   * Returns modified HTML with GCS URLs
   */
  async processHtmlImages(html: string, filename_prefix?: string): Promise<string> {
    // Find all img tags with base64 data
    const imgTagRegex = /<img[^>]+src=["']data:image\/[^;]+;base64,([^"']+)["'][^>]*>/g;
    const matches = Array.from(html.matchAll(imgTagRegex));
    
    if (matches.length === 0) {
      console.log('No base64 images found in HTML');
      return html;
    }

    console.log(`Found ${matches.length} base64 images to process`);
    
    let processedHtml = html;
    
    // Process each image
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const fullImgTag = match[0];
      const base64Data = match[1];
      
      try {
        // Upload image to GCS
        const result = await this.uploadBase64Image({
          base64_data: `data:image/jpeg;base64,${base64Data}`,
          filename_prefix: `${filename_prefix || 'html_image'}_${i + 1}`
        });

        if (result.success && result.public_url) {
          // Replace base64 src with GCS URL
          const updatedImgTag = fullImgTag.replace(
            /src=["']data:image\/[^;]+;base64,[^"']+["']/,
            `src="${result.public_url}"`
          );
          
          processedHtml = processedHtml.replace(fullImgTag, updatedImgTag);
          console.log(`Replaced image ${i + 1} with GCS URL: ${result.public_url}`);
        } else {
          console.warn(`Failed to upload image ${i + 1}:`, result.error);
        }
      } catch (error) {
        console.error(`Error processing image ${i + 1}:`, error);
      }
    }
    
    return processedHtml;
  }

  /**
   * Delete image from GCS
   */
  async deleteImage(filename: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/images/${filename}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Delete failed: ${response.status}`);
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Image delete failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const imageUploadService = new ImageUploadService();